import React, {useEffect, useState} from "react";
import {AnimatePresence, motion} from "framer-motion";
import styles, {C} from "../styles";
import {generateAvatar, getAnonymousAvatar} from "../utils";
import AvatarBadge from "./AvatarBadge";
import useIsMobile from "../hooks/useIsMobile";
import {
    apiCreateCommunityPost,
    apiDeleteCommunityPost,
    apiGetCommunityPosts,
    apiLikeCommunityPost,
} from "../api/communities";
import {apiCreateReport} from "../api/reports";
import {formatDistanceToNow} from "date-fns";
import {fr} from "date-fns/locale";
import {useToast, useConfirm, usePrompt} from "./FeedbackProvider";

export default function Community({community, user}) {
    const {notify} = useToast();
    const confirm = useConfirm();
    const prompt = usePrompt();
    const [posts, setPosts] = useState([]);
    const [content, setContent] = useState("");
    const [isAnonymous, setIsAnonymous] = useState(true);
    const [realName, setRealName] = useState("");
    const isMobile = useIsMobile();

    function loadPosts() {
        apiGetCommunityPosts(community.id).then(data => setPosts(Array.isArray(data) ? data : []));
    }

    useEffect(() => {
        loadPosts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [community.id]);

    function publishPost() {
        if (!user || content.trim() === "") return;
        if (!isAnonymous && realName.trim() === "") return;
        apiCreateCommunityPost(community.id, content, isAnonymous, realName).then((data) => {
            if (data && data.error) { notify(data.error, "error"); return; }
            setContent("");
            setRealName("");
            loadPosts();
        }).catch(() => notify("Impossible de publier le message. Veuillez réessayer.", "error"));
    }

    async function deletePost(postId) {
        if (!(await confirm("Supprimer ce message ?", {danger: true}))) return;
        apiDeleteCommunityPost(postId).then(loadPosts);
    }

    function likePost(postId) {
        apiLikeCommunityPost(postId).then(loadPosts);
    }

    async function reportPost(postId) {
        if (!user) return;
        const reason = await prompt("Pourquoi souhaitez-vous signaler ce message ?");
        if (reason === null) return;
        apiCreateReport("community_post", postId, reason).then(() =>
            notify("Signalement transmis au modérateur.", "success")
        );
    }

    const isSubmitDisabled = content.trim() === "" || (!isAnonymous && realName.trim() === "");

    return (
        <div>
            <div style={{marginBottom: 20}}>
                <h3 style={{margin: "0 0 4px", fontSize: 20, color: C.text, fontFamily: "'Fraunces', serif"}}>💬 {community.title}</h3>
                {community.description && (
                    <p style={{margin: 0, fontSize: 14, color: C.textMuted}}>{community.description}</p>
                )}
            </div>

            {user ? (
                <div style={{
                    backgroundColor: "#FAFAF8",
                    border: `1.5px solid ${C.border}`,
                    borderRadius: 10,
                    padding: 14,
                    marginBottom: 20,
                }}>
                    <div style={{
                        display: "flex",
                        gap: 0,
                        marginBottom: 12,
                        border: `1.5px solid ${C.border}`,
                        borderRadius: 8,
                        overflow: "hidden",
                        width: isMobile ? "100%" : "fit-content",
                    }}>
                        <button
                            onClick={() => { setIsAnonymous(true); setRealName(""); }}
                            style={{
                                flex: isMobile ? 1 : undefined,
                                padding: "8px 16px",
                                border: "none",
                                cursor: "pointer",
                                fontWeight: 600,
                                fontSize: 13,
                                backgroundColor: isAnonymous ? C.primary : "white",
                                color: isAnonymous ? "white" : C.textMuted,
                                transition: "background-color 0.15s",
                            }}
                        >
                            🔒 Anonyme
                        </button>
                        <button
                            onClick={() => setIsAnonymous(false)}
                            style={{
                                flex: isMobile ? 1 : undefined,
                                padding: "8px 16px",
                                border: "none",
                                borderLeft: `1.5px solid ${C.border}`,
                                cursor: "pointer",
                                fontWeight: 600,
                                fontSize: 13,
                                backgroundColor: !isAnonymous ? C.primary : "white",
                                color: !isAnonymous ? "white" : C.textMuted,
                                transition: "background-color 0.15s",
                            }}
                        >
                            ✍️ En mon nom
                        </button>
                    </div>

                    <p style={{fontSize: 12, color: C.textMuted, marginBottom: 10, marginTop: -4}}>
                        {isAnonymous
                            ? <>Votre pseudonyme anonyme : <strong style={{color: C.text}}><AvatarBadge avatar={getAnonymousAvatar({author: user.username})} size={18}/> {user.username}</strong></>
                            : "Votre message sera publié avec le nom que vous entrez ci-dessous."}
                    </p>

                    {!isAnonymous && (
                        <input
                            value={realName}
                            onChange={e => setRealName(e.target.value)}
                            placeholder="Votre prénom et nom (ex : Marie Dupont)"
                            style={{...styles.input, marginBottom: 10}}
                        />
                    )}

                    <textarea
                        value={content}
                        onChange={e => setContent(e.target.value)}
                        placeholder="Exprimez-vous librement…"
                        style={{...styles.input, height: 80, marginBottom: 8}}
                    />
                    <button
                        onClick={publishPost}
                        disabled={isSubmitDisabled}
                        style={{
                            ...styles.primaryButton,
                            opacity: isSubmitDisabled ? 0.5 : 1,
                            cursor: isSubmitDisabled ? "not-allowed" : "pointer",
                        }}
                    >
                        Publier
                    </button>
                </div>
            ) : (
                <p style={{fontSize: 13, color: C.textMuted, fontStyle: "italic", marginBottom: 20}}>
                    Connectez-vous pour participer à cette discussion.
                </p>
            )}

            {posts.length === 0 ? (
                <p style={{color: C.textMuted, fontStyle: "italic", fontSize: 14}}>
                    Aucun message pour l'instant — soyez le premier !
                </p>
            ) : (
                <AnimatePresence initial={false}>
                    {posts.map(post => {
                        const anonymous = post.is_anonymous === 1;
                        const avatarData = anonymous ? getAnonymousAvatar(post) : generateAvatar(post.author);
                        return (
                            <motion.div
                                key={post.id}
                                initial={{opacity: 0, y: 10}}
                                animate={{opacity: 1, y: 0}}
                                exit={{opacity: 0, height: 0, marginBottom: 0}}
                                transition={{duration: 0.18}}
                                style={{
                                    border: `1px solid ${C.border}`,
                                    borderRadius: 10,
                                    padding: "12px 16px",
                                    marginBottom: 10,
                                    backgroundColor: "#FAFAF8",
                                }}
                            >
                                <div style={{display: "flex", alignItems: "center", gap: 10, marginBottom: 8}}>
                                    <div style={{
                                        ...styles.avatarCircle,
                                        width: 38, height: 38, fontSize: 18,
                                        backgroundColor: avatarData.color,
                                    }}>
                                        <AvatarBadge avatar={avatarData}/>
                                    </div>
                                    <div>
                                        <div style={{display: "flex", alignItems: "center", gap: 8}}>
                                            <strong style={{fontSize: 14, color: C.text}}>{post.author}</strong>
                                            {anonymous && (
                                                <span style={{
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    backgroundColor: "#F3F4F6",
                                                    color: "#6B7280",
                                                    border: "1px solid #D1D5DB",
                                                    borderRadius: 20,
                                                    padding: "1px 8px",
                                                    letterSpacing: 0.3,
                                                }}>
                                                    Anonyme
                                                </span>
                                            )}
                                        </div>
                                        <div style={{fontSize: 12, color: C.textMuted}}>
                                            {formatDistanceToNow(new Date(post.created_at), {
                                                addSuffix: true,
                                                locale: fr
                                            })}
                                        </div>
                                    </div>
                                </div>
                                <p style={{margin: "0 0 10px", fontSize: 14, lineHeight: 1.5}}>{post.content}</p>
                                <div style={{display: "flex", gap: 6, flexWrap: "wrap"}}>
                                    <button onClick={() => likePost(post.id)} style={styles.likeButton}>
                                        ❤️ Soutenir ({post.likes})
                                    </button>
                                    <button onClick={() => reportPost(post.id)} style={styles.button}>
                                        🚩 Signaler
                                    </button>
                                    {user?.role === "admin" && (
                                        <button onClick={() => deletePost(post.id)} style={styles.dangerButton}>
                                            Supprimer
                                        </button>
                                    )}
                                </div>
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            )}
        </div>
    );
}
